import React, { useMemo, useState } from "react";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function toDate(value) {
  if (!value) return null;

  try {
    const parsed =
      typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);

    return Number.isNaN(parsed.getTime())
      ? null
      : parsed;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const parsed = toDate(value);

  return parsed
    ? parsed.toLocaleString()
    : "Date unavailable";
}

function titleCase(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function getIncidentDate(incident) {
  return (
    incident?.incidentDate ||
    incident?.occurredAt ||
    incident?.reportedAt ||
    incident?.createdAt ||
    incident?.updatedAt
  );
}

function getIncidentStatus(incident) {
  return normalize(
    incident?.status ||
      incident?.incidentStatus ||
      incident?.investigationStatus ||
      "open"
  );
}

function getIncidentSeverity(incident) {
  return normalize(
    incident?.severity ||
      incident?.priority ||
      incident?.riskLevel ||
      "standard"
  );
}

function getStatusTone(status) {
  if (
    ["closed", "resolved", "completed"].includes(status)
  ) {
    return {
      color: "#166534",
      background: "#dcfce7",
      border: "#bbf7d0",
    };
  }

  if (
    ["investigating", "under_review", "pending"].includes(
      status
    )
  ) {
    return {
      color: "#92400e",
      background: "#fef3c7",
      border: "#fde68a",
    };
  }

  return {
    color: "#991b1b",
    background: "#fee2e2",
    border: "#fecaca",
  };
}

function getSeverityTone(severity) {
  if (
    ["critical", "high", "urgent"].includes(severity)
  ) {
    return "#b91c1c";
  }

  if (["medium", "moderate"].includes(severity)) {
    return "#b45309";
  }

  return "#475569";
}

export default function Facility360Incidents({
  facility,
  incidents = [],
}) {
  const [statusFilter, setStatusFilter] =
    useState("all");

const facilityId = normalize(
  facility?.facilityId || facility?.id
);

const facilityName = normalize(
  facility?.facilityName || facility?.name
);

const tenantId = normalize(
  facility?.tenantId ||
    facility?.tenantKey
);

  const facilityIncidents = useMemo(() => {
    const rows = Array.isArray(incidents)
      ? incidents
      : [];

    return rows
.filter((incident) => {
  const incidentFacilityId = normalize(
    incident?.facilityId ||
      incident?.assignedFacilityId ||
      incident?.locationId
  );

  const incidentFacilityName = normalize(
    incident?.facilityName ||
      incident?.locationName
  );

  const incidentTenantId = normalize(
    incident?.tenantId ||
      incident?.tenantKey
  );

  if (
    tenantId &&
    incidentTenantId &&
    incidentTenantId !== tenantId
  ) {
    return false;
  }

  if (facilityId && incidentFacilityId) {
    return incidentFacilityId === facilityId;
  }

  return Boolean(
    facilityName &&
      !incidentFacilityId &&
      incidentFacilityName === facilityName
  );
})
      .sort((a, b) => {
        const aDate = toDate(getIncidentDate(a));
        const bDate = toDate(getIncidentDate(b));

        return (
          (bDate?.getTime() || 0) -
          (aDate?.getTime() || 0)
        );
      });
  }, [
  incidents,
  facilityId,
  facilityName,
  tenantId,
]);

  const metrics = useMemo(() => {
    const open = facilityIncidents.filter(
      (incident) =>
        ![
          "closed",
          "resolved",
          "completed",
        ].includes(getIncidentStatus(incident))
    );

    const resolved = facilityIncidents.filter(
      (incident) =>
        [
          "closed",
          "resolved",
          "completed",
        ].includes(getIncidentStatus(incident))
    );

    const highRisk = facilityIncidents.filter(
      (incident) =>
        [
          "critical",
          "high",
          "urgent",
        ].includes(getIncidentSeverity(incident))
    );

    return {
      total: facilityIncidents.length,
      open: open.length,
      resolved: resolved.length,
      highRisk: highRisk.length,
    };
  }, [facilityIncidents]);

  const visibleIncidents = useMemo(() => {
    if (statusFilter === "all") {
      return facilityIncidents;
    }

    if (statusFilter === "open") {
      return facilityIncidents.filter(
        (incident) =>
          ![
            "closed",
            "resolved",
            "completed",
          ].includes(getIncidentStatus(incident))
      );
    }

    if (statusFilter === "resolved") {
      return facilityIncidents.filter(
        (incident) =>
          [
            "closed",
            "resolved",
            "completed",
          ].includes(getIncidentStatus(incident))
      );
    }

    return facilityIncidents;
  }, [facilityIncidents, statusFilter]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Incident History
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Operational, worker, safety, and client
          incidents recorded for this facility.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        {[
          ["Total Incidents", metrics.total, "#1d4ed8"],
          ["Open", metrics.open, "#b91c1c"],
          ["Resolved", metrics.resolved, "#166534"],
          ["High Risk", metrics.highRisk, "#b45309"],
        ].map(([label, value, color]) => (
          <div
            key={label}
            style={{
              padding: 13,
              borderRadius: 14,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}
          >
            <div
              style={{
                color: "#64748b",
                fontSize: 10,
                fontWeight: 900,
                textTransform: "uppercase",
              }}
            >
              {label}
            </div>

            <div
              style={{
                marginTop: 5,
                color,
                fontSize: 22,
                fontWeight: 950,
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          gap: 7,
          overflowX: "auto",
          paddingBottom: 3,
        }}
      >
        {[
          ["all", "All"],
          ["open", "Open"],
          ["resolved", "Resolved"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setStatusFilter(key)}
            style={{
              minHeight: 34,
              padding: "6px 12px",
              borderRadius: 999,
              border:
                statusFilter === key
                  ? "1px solid #2563eb"
                  : "1px solid #cbd5e1",
              background:
                statusFilter === key
                  ? "#2563eb"
                  : "#ffffff",
              color:
                statusFilter === key
                  ? "#ffffff"
                  : "#334155",
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 900,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {visibleIncidents.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
            color: "#64748b",
            textAlign: "center",
            fontWeight: 750,
          }}
        >
          No matching incident records were found for
          this facility.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {visibleIncidents.map(
            (incident, index) => {
              const status =
                getIncidentStatus(incident);

              const severity =
                getIncidentSeverity(incident);

              const statusTone =
                getStatusTone(status);

              const workerName =
                incident?.workerName ||
                incident?.employeeName ||
                incident?.staffName ||
                "";

              const title =
                incident?.title ||
                incident?.incidentTitle ||
                incident?.type ||
                incident?.incidentType ||
                "Facility Incident";

              const description =
                incident?.description ||
                incident?.summary ||
                incident?.details ||
                incident?.notes ||
                "No incident description is available.";

              return (
                <div
                  key={
                    incident?.id ||
                    `incident-${index}`
                  }
                  style={{
                    padding: 14,
                    borderRadius: 15,
                    border: "1px solid #e2e8f0",
                    background: "#ffffff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "space-between",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#0f172a",
                          fontSize: 14,
                          fontWeight: 950,
                        }}
                      >
                        {titleCase(title)}
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          color: "#64748b",
                          fontSize: 11,
                          fontWeight: 750,
                        }}
                      >
                        {formatDateTime(
                          getIncidentDate(incident)
                        )}
                      </div>
                    </div>

                    <span
                      style={{
                        padding: "5px 8px",
                        borderRadius: 999,
                        border: `1px solid ${statusTone.border}`,
                        background:
                          statusTone.background,
                        color: statusTone.color,
                        fontSize: 9,
                        fontWeight: 950,
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {titleCase(status)}
                    </span>
                  </div>

                  <div
                    style={{
                      marginTop: 10,
                      color: "#475569",
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.5,
                    }}
                  >
                    {String(description)}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 7,
                      marginTop: 11,
                    }}
                  >
                    <span
                      style={{
                        padding: "5px 8px",
                        borderRadius: 999,
                        background: "#f1f5f9",
                        color:
                          getSeverityTone(severity),
                        fontSize: 10,
                        fontWeight: 900,
                      }}
                    >
                      Severity: {titleCase(severity)}
                    </span>

                    {workerName ? (
                      <span
                        style={{
                          padding: "5px 8px",
                          borderRadius: 999,
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          fontSize: 10,
                          fontWeight: 900,
                        }}
                      >
                        Worker: {String(workerName)}
                      </span>
                    ) : null}

                    {incident?.reportedByName ||
                    incident?.reportedBy ? (
                      <span
                        style={{
                          padding: "5px 8px",
                          borderRadius: 999,
                          background: "#f8fafc",
                          color: "#475569",
                          fontSize: 10,
                          fontWeight: 900,
                        }}
                      >
                        Reported by:{" "}
                        {String(
                          incident.reportedByName ||
                            incident.reportedBy
                        )}
                      </span>
                    ) : null}
                  </div>

                  {incident?.resolution ||
                  incident?.resolutionNotes ? (
                    <div
                      style={{
                        marginTop: 11,
                        padding: 11,
                        borderRadius: 11,
                        border:
                          "1px solid #bbf7d0",
                        background: "#f0fdf4",
                        color: "#166534",
                        fontSize: 11,
                        fontWeight: 750,
                        lineHeight: 1.45,
                      }}
                    >
                      <strong>Resolution:</strong>{" "}
                      {String(
                        incident.resolution ||
                          incident.resolutionNotes
                      )}
                    </div>
                  ) : null}
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}